/**
 * Раздел «Земельный фонд» страницы «Аналитика» (US-04.6): верхняя плашка
 * из 4 показателей (всего земель, пашни, пастбища, земли запаса) + два
 * виджета-разреза в ряд («Земли населённых пунктов», «Земли запаса»).
 *
 * Карточка плашки «Сельхоз. предприятия» и широкая таблица «Крупные
 * землевладельцы» убраны со страницы (DP-513): счётчик сельхозпредприятий
 * остался строкой внутри виджета «Земли населённых пунктов», поле
 * `landowners` ответа API больше не читается.
 *
 * Показатели плашки видны всегда (без данных — «—», замечание ревью);
 * карточки равномерно
 * растягиваются по ширине (FR-2.4); виджет без данных не рендерится
 * (Сценарий 8). Drill-through (FR-4) — вне текущей вёрстки: детализация
 * первичных данных появится вместе с бэком.
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsLandFund.json (тыс. га; «итого» вычисляются
 * на фронте, FR-2.3/FR-3.5); остальные КАТО (по выбранному в сайдбаре) —
 * один запрос /api/public/analytics/land-fund (DP-136, US-04.6):
 * plashka → плашка, settlement → «Земли населённых пунктов», reserve →
 * «Земли запаса». Площади API — в га, лейбл на фронте — «тыс. га»
 * (÷1000); «итого» приходят готовыми.
 */
import { LandPlot } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import analyticsLandFund from '../../../hardData/analyticsLandFund.json';
import { getAnalyticsLandFund } from '../../../requests/analytics.request';
import { ModuleGate } from '../../../components/common/ModuleGate';
import { MODULES } from '../../../consts/modules';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type { LandFundAnalyticsResponse } from '../../../types/analytics';

/** Разрезы земель — отдельные виджеты витрины (US-09 FR-1). */
const LAND_FUND_MODULE: Record<'settlementLands' | 'reserveLands', string> = {
  settlementLands: MODULES.analytics.landFundSettlement,
  reserveLands: MODULES.analytics.landFundReserve,
};

const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

/** Сумма компонент; все null → null (в плашке показывается «—»). */
const sum = (...parts: Array<number | null>): number | null => {
  const known = parts.filter((v): v is number => v !== null);
  return known.length === 0
    ? null
    : known.reduce((acc, v) => acc + v, 0);
};

/** Га из API → тыс. га для отображения (лейбл «тыс. га» на фронте). */
const toThsHa = (v: number | null): number | null =>
  v !== null ? v / 1000 : null;

interface LandBarRowVM {
  key: string;
  value: string;
  unit: string;
  barPercent: number;
}

interface LandFundSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевой виджет —
   * «Земли населённых пунктов» (FR-2.2). */
  summary?: boolean;
}

export const LandFundSection = ({
  summary = false,
}: LandFundSectionProps = {}) => {
  const { t } = useTranslation();
  // Плашка ключевых показателей — отдельный виджет витрины (US-09 FR-1).
  const showPlashka = useModulePresent(MODULES.analytics.landFundPlashka);

  const { data, loading, isHardKato } =
    useAnalyticsSection<LandFundAnalyticsResponse>(
      'landFund',
      getAnalyticsLandFund,
    );

  const thousandHa = t('analytics.landFund.unitThousandHa');

  // --- Значения в тыс. га: мок — как есть (агрегаты вычисляются,
  // FR-2.3/FR-3.5), API — га ÷ 1000 («итого» приходят готовыми) ---

  const {
    settlementLand,
    agricultural_arable: mockAgriArable,
    agricultural_pasture: mockAgriPasture,
    reserve_arable: mockReserveArable,
    reserve_pasture: mockReservePasture,
    otherLand: mockOtherLand,
  } = analyticsLandFund.landFundItems;

  const settlement = data?.settlement ?? null;
  const reserve = data?.reserve ?? null;
  const plashka = data?.plashka;

  const agriArable = isHardKato
    ? mockAgriArable
    : toThsHa(settlement?.agricultural_arable_ha ?? null);
  const agriPasture = isHardKato
    ? mockAgriPasture
    : toThsHa(settlement?.agricultural_pasture_ha ?? null);
  const agriTotal = isHardKato
    ? sum(agriArable, agriPasture)
    : toThsHa(settlement?.agricultural_total_ha ?? null);
  const reserveArable = isHardKato
    ? mockReserveArable
    : toThsHa(reserve?.reserve_arable_ha ?? null);
  const reservePasture = isHardKato
    ? mockReservePasture
    : toThsHa(reserve?.reserve_pasture_ha ?? null);
  const reserveTotal = isHardKato
    ? sum(reserveArable, reservePasture)
    : toThsHa(reserve?.reserve_total_ha ?? plashka?.reserve_total_ha ?? null);
  const otherLand = isHardKato
    ? mockOtherLand
    : toThsHa(reserve?.other_land_ha ?? null);

  const totalLand = isHardKato
    ? sum(settlementLand, reserveTotal, otherLand)
    : toThsHa(plashka?.total_land_ha ?? null);
  const plashkaArable = isHardKato
    ? agriArable
    : toThsHa(plashka?.arable_ha ?? null);
  const plashkaPasture = isHardKato
    ? agriPasture
    : toThsHa(plashka?.pasture_ha ?? null);

  // Плашка всегда показывает все показатели; нет данных → «—»
  // (замечание дизайн-ревью). Карточка «Сельхоз. предприятия» убрана
  // (DP-513), поэтому все показатели плашки — в тыс. га.
  const landCell = (v: number | null): string => (v === null ? '—' : fmt1(v));
  const cards: Array<{ key: string; value: string; unit: string }> = [
    { key: 'totalLand', value: landCell(totalLand), unit: thousandHa },
    { key: 'arable', value: landCell(plashkaArable), unit: thousandHa },
    { key: 'pasture', value: landCell(plashkaPasture), unit: thousandHa },
    { key: 'reserveLand', value: landCell(reserveTotal), unit: thousandHa },
  ];

  // Виджеты-разрезы: бары — доля от максимума среди строк виджета. Все
  // строки в тыс. га: строка «С/х предприятия» («ед.») убрана (DP-513),
  // вместе с ней — обработка строк в других единицах.
  const toBars = (
    rows: Array<{ key: string; value: number | null }>,
  ): LandBarRowVM[] => {
    const known = rows.filter(
      (row): row is { key: string; value: number } => row.value !== null,
    );
    const max = Math.max(1, ...known.map((row) => row.value));
    return known.map((row) => ({
      key: row.key,
      value: fmt1(row.value),
      unit: thousandHa,
      barPercent: Math.max(2, (row.value / max) * 100),
    }));
  };

  const settlementRows = toBars([
    { key: 'agriTotal', value: agriTotal },
    { key: 'agriArable', value: agriArable },
    { key: 'agriPasture', value: agriPasture },
  ]);

  const reserveRows = toBars([
    { key: 'total', value: reserveTotal },
    { key: 'arable', value: reserveArable },
    { key: 'pasture', value: reservePasture },
    { key: 'otherLand', value: otherLand },
  ]);

  const widgetDefs: Array<{
    key: 'settlementLands' | 'reserveLands';
    rows: LandBarRowVM[];
    testId: string;
  }> = [
    {
      key: 'settlementLands',
      rows: settlementRows,
      testId: 'analytics-landfund-settlement',
    },
    {
      key: 'reserveLands',
      rows: reserveRows,
      testId: 'analytics-landfund-reserve',
    },
  ];
  // Виджеты всегда видны (без данных — «Нет данных», замечание ревью);
  // на витрине «Все» остаётся только «Земли населённых пунктов»
  // (US-04.1 FR-2.2).
  const widgets = widgetDefs.filter(
    (widget) => !summary || widget.key === 'settlementLands',
  );

  return (
    <section className="space-y-4" data-testid="analytics-landfund">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.landFund.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <LandPlot
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>

      {/* Плашка: все показатели всегда видны, без данных — «—». */}
      {!summary && showPlashka && (
        <div className="flex flex-wrap gap-3.5">
          {cards.map((card) => (
            <div key={card.key} className="min-w-52 flex-1">
              <StatCard
                testId={`analytics-card-${card.key}`}
                title={t(`analytics.landFund.cards.${card.key}`)}
                value={card.value}
                unit={card.unit}
              />
            </div>
          ))}
        </div>
      )}

      {widgets.length > 0 && (
        <div className="grid gap-3.5 xl:grid-cols-2">
          {widgets.map((widget) => (
            <ModuleGate key={widget.key} id={LAND_FUND_MODULE[widget.key]}>
            <Card className="shadow-card">
              <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
                <CardTitle>
                  {t(`analytics.landFund.${widget.key}.title`)}
                </CardTitle>
                <span className="shrink-0 text-sm text-text-faint">
                  {thousandHa}
                </span>
              </CardHeader>
              <CardContent>
                {widget.rows.length === 0 ? (
                  <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
                ) : (
                <ul data-testid={widget.testId}>
                  {widget.rows.map((row) => (
                    <li
                      key={row.key}
                      data-testid={`${widget.testId}-${row.key}`}
                      // Сетка из трёх колонок: полосы всех строк одной ширины и
                      // начинаются от одной вертикали, под число отведена
                      // колонка фиксированной ширины (замечание 3.2.5).
                      className="grid grid-cols-[minmax(0,1fr)_33%_7.5rem] items-center gap-4 border-b border-surface-border py-3.5 text-sm last:border-b-0 last:pb-0"
                    >
                      <span className="min-w-0 font-medium leading-snug">
                        {t(`analytics.landFund.${widget.key}.rows.${row.key}`)}
                      </span>
                      <span className="h-4 overflow-hidden rounded-full bg-surface-muted">
                        <span
                          className="block h-full rounded-full bg-chart-blue"
                          style={{ width: `${row.barPercent}%` }}
                        />
                      </span>
                      <span className="whitespace-nowrap text-right">
                        <span className="font-extrabold tabular-nums">
                          {row.value}
                        </span>{' '}
                        <span className="text-text-faint">{row.unit}</span>
                      </span>
                    </li>
                  ))}
                </ul>
                )}
              </CardContent>
            </Card>
            </ModuleGate>
          ))}
        </div>
      )}

        </>
      )}
    </section>
  );
};
